import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Lock, ShieldCheck, KeyRound, Link2, FileText, Server,
  Layers, Users, Gamepad2, HelpCircle, BookOpen, Printer, ArrowLeft,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { verifyGuia } from "@/lib/api";
import { toast } from "sonner";
import logo from "@/assets/sortiven-logo.png";

function Section({ icon: Icon, num, title, children }) {
  return (
    <section className="scroll-mt-24" data-testid={`guia-section-${num}`}>
      <div className="mb-4 flex items-center gap-3 border-b border-border pb-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="font-display text-lg font-semibold text-foreground">
          <span className="mr-2 text-primary">{num}.</span>{title}
        </h2>
      </div>
      <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/50">
          <tr>{head.map((h, i) => <th key={i} className="px-4 py-2 font-semibold text-foreground">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-muted/30">
              {r.map((c, j) => <td key={j} className="px-4 py-2 align-top">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Code({ children }) {
  return (
    <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100">
      <code>{children}</code>
    </pre>
  );
}

function GuiaContent() {
  return (
    <div className="mx-auto max-w-4xl space-y-12 px-4 py-10">
      {/* Encabezado */}
      <header className="flex flex-col items-center gap-4 text-center print:hidden">
        <img src={logo} alt="SORTIVEN" className="h-16 w-auto" />
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
            Guía de Presentación Técnica
          </h1>
          <p className="mt-2 text-base text-muted-foreground">
            SORTIVEN V1.4 — Exposición ante CONALOT
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => window.print()} data-testid="guia-print-btn">
          <Printer className="h-4 w-4" /> Imprimir / Guardar PDF
        </Button>
      </header>

      <Section icon={BookOpen} num={0} title="Resumen ejecutivo">
        <p>
          SORTIVEN es una plataforma de sorteos digitales cuya premisa central es la{" "}
          <strong className="text-foreground">aleatoriedad verificable por cualquier tercero (Provably Fair)</strong>.
          A diferencia de un sistema tradicional donde el operador dice "confíen en nosotros", SORTIVEN
          permite que el regulador, el auditor y hasta el público recomputen el resultado de forma
          independiente y comprueben, matemáticamente, que <strong className="text-foreground">nadie pudo
          manipular el sorteo</strong> — ni siquiera el propio operador.
        </p>
        <p className="font-medium text-foreground">Tres pilares:</p>
        <ol className="list-decimal space-y-1 pl-6">
          <li><strong className="text-foreground">CSPRNG + Provably Fair</strong> → aleatoriedad de calidad criptográfica + verificable.</li>
          <li><strong className="text-foreground">Bitácora inmutable (hash encadenado)</strong> → cualquier alteración histórica se detecta.</li>
          <li><strong className="text-foreground">Seguridad de grado bancario + control de roles</strong> → integridad operativa.</li>
        </ol>
      </Section>

      <Section icon={Server} num={1} title="Arquitectura general">
        <Code>{`   Público / Auditor / Regulador
              │
        [ Frontend React ]   ← Vista pública (moderna) + Consola (institucional)
              │  HTTPS
        [ API FastAPI ]      ← Lógica, RBAC, seguridad, RNG
              │
     [ PostgreSQL / Supabase ]  ← usuarios, juegos, sorteos, bitácora`}</Code>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">Frontend:</strong> React + TailwindCSS. Doble interfaz: pública (moderna, para el ciudadano) y consola (institucional, para operadores/auditores).</li>
          <li><strong className="text-foreground">Backend:</strong> FastAPI (Python 3.11), asíncrono. Toda ruta protegida bajo <code className="rounded bg-muted px-1">/api</code>.</li>
          <li><strong className="text-foreground">Base de datos:</strong> PostgreSQL gestionado (Supabase), conexión cifrada.</li>
          <li><strong className="text-foreground">Núcleo RNG</strong> aislado y versionado (<code className="rounded bg-muted px-1">ALGO_VERSION = "rng-v1"</code>), probado de forma independiente. No se modifica sin re-versionar, para no romper la reproducibilidad de sorteos históricos.</li>
        </ul>
      </Section>

      <Section icon={KeyRound} num={2} title="Aleatoriedad verificable — el corazón del sistema">
        <p>
          <strong className="text-foreground">¿Por qué "Provably Fair"?</strong> El operador se compromete con la
          semilla ANTES del sorteo publicando su huella (hash), pero sin revelar la semilla. Después del
          sorteo, revela la semilla. Como la huella ya estaba publicada, el operador no pudo cambiar la
          semilla para forzar un resultado. Esto se puede probar matemáticamente.
        </p>
        <p className="font-medium text-foreground">Esquema criptográfico paso a paso:</p>
        <Table
          head={["#", "Qué ocurre", "Técnica"]}
          rows={[
            ["1", "Semilla del servidor (aleatoria)", "CSPRNG — secrets.token_hex(32) (256 bits)"],
            ["2", "Compromiso publicado ANTES del sorteo", "SHA-256(server_seed)"],
            ["3", "Semilla del cliente (entropía externa)", "client_seed público"],
            ["4", "Nonce (contador único por sorteo)", "nonce"],
            ["5", "Flujo de bits (derivación del azar)", "HMAC-SHA256(server_seed, \"client_seed:nonce:contador\")"],
            ["6", "Mapeo a números sin sesgo", "Rejection sampling (NO módulo)"],
            ["7", "Revelación de la semilla", "Cualquiera recomputa y verifica"],
          ]}
        />
        <p className="font-medium text-foreground">Puntos clave para destacar ante el regulador:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">CSPRNG, no random():</strong> se usa <code className="rounded bg-muted px-1">secrets</code> (fuente criptográficamente segura del SO), no un generador pseudoaleatorio predecible.</li>
          <li><strong className="text-foreground">HMAC-SHA256:</strong> estándar de la industria (FIPS 198-1). Determinístico: mismas entradas → misma salida → reproducible.</li>
          <li><strong className="text-foreground">Rejection sampling:</strong> evita el "modulo bias". Un <code className="rounded bg-muted px-1">número % rango</code> favorece números bajos; nuestro algoritmo descarta valores fuera del rango uniforme → distribución perfectamente uniforme, auditable estadísticamente.</li>
          <li><strong className="text-foreground">Entropía dual:</strong> semilla del servidor (secreta hasta la revelación) + semilla del cliente (pública) → ninguna parte controla el resultado por sí sola.</li>
        </ul>
        <p className="font-medium text-foreground">Fórmula pública de verificación:</p>
        <Code>{`1) COMPROMISO:  SHA-256(server_seed_revelado) == commitment_publicado ?   ✔
2) RESULTADO:   recomputar con HMAC-SHA256(server_seed, "client_seed:nonce:contador")
                → mapeo por rejection sampling → ¿coincide con resultado_oficial? ✔
3) CADENA:      record_hash == SHA-256(prev_hash + json_canónico(registro)) ?   ✔`}</Code>
        <p>El portal público lo hace <strong className="text-foreground">automáticamente</strong> en <code className="rounded bg-muted px-1">GET /api/verify</code>.</p>
      </Section>

      <Section icon={Link2} num={3} title="Trazabilidad — bitácora inmutable">
        <p>Cada sorteo genera un registro en la bitácora encadenada (ledger), inspirada en la estructura de una cadena de bloques:</p>
        <Code>{`record_hash = SHA-256( prev_hash + json_canónico(registro) )`}</Code>
        <ul className="list-disc space-y-2 pl-6">
          <li>Cada registro <strong className="text-foreground">enlaza con el anterior</strong> por su hash (<code className="rounded bg-muted px-1">prev_hash</code>).</li>
          <li>El primer registro parte de un <code className="rounded bg-muted px-1">GENESIS_HASH</code> (64 ceros).</li>
          <li><strong className="text-foreground">JSON canónico:</strong> claves ordenadas y sin espacios → el hash es reproducible byte a byte.</li>
          <li><strong className="text-foreground">Propiedad clave:</strong> si alguien altera un sorteo histórico (aunque sea un dígito), su hash cambia y se rompe toda la cadena posterior → la manipulación es matemáticamente detectable.</li>
        </ul>
        <p>Además, cada acción sensible queda en <code className="rounded bg-muted px-1">audit_events</code> (quién, qué, cuándo, desde qué IP).</p>
      </Section>

      <Section icon={FileText} num={4} title="Actas y paquete de auditoría">
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">Acta PDF certificada</strong> (<code className="rounded bg-muted px-1">/api/draws/&#123;id&#125;/acta.pdf</code>): documento oficial con todos los datos, hashes y resultado, con logo institucional.</li>
          <li>
            <strong className="text-foreground">Paquete de auditoría ZIP</strong> (<code className="rounded bg-muted px-1">/api/draws/&#123;id&#125;/bundle.zip</code>) contiene:
            <ul className="mt-2 list-[circle] space-y-1 pl-6">
              <li><code className="rounded bg-muted px-1">acta.pdf</code> — acta certificada.</li>
              <li><code className="rounded bg-muted px-1">sorteo.json</code> — datos en formato canónico + bloque de verificación.</li>
              <li><code className="rounded bg-muted px-1">prueba_cadena.json</code> — enlace en la bitácora + recálculo del hash.</li>
              <li><code className="rounded bg-muted px-1">LEEME.txt</code> — instrucciones para verificar de forma independiente.</li>
            </ul>
          </li>
        </ul>
        <p>El regulador puede llevarse la evidencia y <strong className="text-foreground">verificarla con sus propias herramientas</strong> (cualquier lenguaje con SHA-256/HMAC).</p>
      </Section>

      <Section icon={ShieldCheck} num={5} title="Seguridad de grado bancario">
        <Table
          head={["Control", "Implementación"]}
          rows={[
            ["Rate limiting global (anti-DoS)", "1000 req / 60 s por IP"],
            ["Rate limiting de login", "30 intentos / 300 s por IP"],
            ["Bloqueo de cuenta (anti fuerza bruta)", "5 fallos consecutivos → bloqueo 15 min"],
            ["Política de contraseñas", "Mín. 10 caracteres, mayúscula, minúscula, número, símbolo; rechaza comunes"],
            ["Límite de tamaño de cuerpo", "1 MiB (anti abuso)"],
            ["Límites anti-DoS del RNG", "Acota rangos, selecciones y figuras"],
            ["Cabeceras de seguridad HTTP", "HSTS, X-Frame-Options: DENY, X-Content-Type-Options, CSP, Referrer-Policy, Permissions-Policy, Cache-Control: no-store"],
          ]}
        />
        <p className="font-medium text-foreground">Autenticación:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">JWT firmado (HS256)</strong> con emisor/audiencia validados y claims obligatorios (exp, iat, nbf, sub, iss, aud).</li>
          <li>Contraseñas con <strong className="text-foreground">bcrypt</strong> (hash + sal, coste adaptativo). Nunca se almacena la contraseña en claro.</li>
          <li><strong className="text-foreground">Cierre de sesión por inactividad</strong> (15 min) en el frontend.</li>
        </ul>
      </Section>

      <Section icon={Users} num={6} title="Control de roles (RBAC)">
        <Table
          head={["Rol", "Puede"]}
          rows={[
            ["Administrador", "Acceso total: gestiona usuarios, juegos y sorteos."],
            ["Operador", "Crea, ejecuta y revela sorteos; gestiona juegos. No gestiona usuarios."],
            ["Auditor / Veedor", "Solo lectura. Consulta todo y exporta actas/bitácora."],
          ]}
        />
        <ul className="list-disc space-y-2 pl-6">
          <li>El rol de <strong className="text-foreground">Auditor</strong> es clave para el regulador: supervisa sin poder alterar nada. Ideal para un veedor de CONALOT.</li>
          <li>Las rutas públicas (sitio, verificación, bitácora, acta PDF) no requieren autenticación → transparencia total hacia el ciudadano.</li>
        </ul>
      </Section>

      <Section icon={Gamepad2} num={7} title="Juegos implementados">
        <ol className="list-decimal space-y-2 pl-6">
          <li><strong className="text-foreground">TripleFalcón Venezuela:</strong> Triple A y Triple B (000–999) + Estado (24 entidades de Venezuela). Los terminales (2 últimos dígitos) son derivados, no se sortean aparte.</li>
          <li><strong className="text-foreground">ZooloFalcón Venezuela:</strong> una figura ganadora entre el universo de animalitos, con figura COMODÍN (Chivo, #19) según el prospecto.</li>
        </ol>
        <p className="rounded-lg border border-border bg-muted/30 p-3">
          Los módulos "Rifas" y "Raspaditos" están <strong className="text-foreground">ocultos temporalmente</strong> en la UI
          (implementados en el motor, listos para activarse cuando se autoricen).
        </p>
      </Section>

      <Section icon={Layers} num={8} title="Demostración en vivo (guion sugerido)">
        <ol className="list-decimal space-y-2 pl-6">
          <li>Vista pública → mostrar inicio y catálogo de juegos.</li>
          <li>Login como Operador → crear un sorteo → mostrar el commitment publicado ANTES de ejecutar.</li>
          <li>Ejecutar el sorteo → se genera el resultado.</li>
          <li>Revelar la semilla → el sistema publica <code className="rounded bg-muted px-1">server_seed</code>.</li>
          <li>Portal de verificación (<code className="rounded bg-muted px-1">/verificar</code>) → introducir el ID → mostrar los 3 checks en verde.</li>
          <li>Descargar el paquete ZIP → abrir <code className="rounded bg-muted px-1">LEEME.txt</code> y explicar la verificación independiente.</li>
          <li>Login como Auditor → mostrar acceso de solo lectura + bitácora.</li>
        </ol>
      </Section>

      <Section icon={HelpCircle} num={9} title="Preguntas frecuentes del regulador">
        <div className="space-y-4">
          {[
            ["¿Cómo garantizan que el operador no manipuló el resultado?", "Porque el compromiso (hash de la semilla) se publica antes del sorteo. Si el operador cambiara la semilla para forzar un número, el hash ya no coincidiría, y cualquiera lo detectaría. Es imposible \"cuadrar\" un resultado a posteriori."],
            ["¿Qué pasa si alguien altera la base de datos?", "La bitácora encadenada rompe su continuidad de hashes. La manipulación se detecta al recomputar la cadena. Además, todo queda en audit_events con IP y usuario."],
            ["¿El generador es realmente aleatorio?", "Usamos CSPRNG del sistema operativo (secrets) para la semilla, HMAC-SHA256 para el flujo, y rejection sampling para evitar sesgos. La distribución es uniforme y auditable estadísticamente."],
            ["¿Pueden verificar sin acceso a su sistema?", "Sí. El paquete ZIP incluye todos los datos y las instrucciones. Cualquier herramienta con SHA-256/HMAC recomputa el resultado. No dependen de nosotros."],
            ["¿Y si el servidor cae durante un sorteo?", "El sorteo solo se considera válido cuando queda registrado en la bitácora con su hash. Estados intermedios no producen resultado oficial."],
            ["¿Dónde se guardan los datos?", "PostgreSQL gestionado con conexión cifrada y respaldos automáticos."],
          ].map(([q, a], i) => (
            <div key={i} className="rounded-lg border border-border p-4">
              <p className="font-semibold text-foreground">P: {q}</p>
              <p className="mt-1">R: {a}</p>
            </div>
          ))}
        </div>
      </Section>

      <footer className="border-t border-border pt-6 text-center text-xs text-muted-foreground">
        SORTIVEN V1.4 — Documento de apoyo para exposición técnica. Uso interno.
      </footer>
    </div>
  );
}

export default function GuiaTecnica() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyGuia(password);
      setUnlocked(true);
    } catch {
      toast.error("Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) {
    return (
      <div data-theme="institutional" className="min-h-screen bg-background" data-testid="guia-content">
        <div className="border-b border-border bg-card/50 print:hidden">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
            <Link to="/" className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="guia-home-link">
              <ArrowLeft className="h-4 w-4" /> Volver al sitio
            </Link>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Acceso autorizado
            </span>
          </div>
        </div>
        <GuiaContent />
      </div>
    );
  }

  return (
    <div data-theme="institutional" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center">
          <img src={logo} alt="SORTIVEN" className="h-14 w-auto" />
        </Link>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-xl">
              <Lock className="h-5 w-5 text-primary" /> Guía Técnica — Acceso restringido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Documento de apoyo para la presentación ante CONALOT. Introduzca la contraseña para continuar.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="guia-password">Contraseña</Label>
                <Input
                  id="guia-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  data-testid="guia-password-input"
                />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="guia-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Acceder
              </Button>
            </form>
            <Link to="/" className="mt-4 block text-center text-xs text-primary hover:underline">← Volver al sitio público</Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
